import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Home/Footer';
import BrowserNotSupported from './BrowserNotSupported';
import ScoreCard from '../components/IELTS/ScoreCard';
import TestCard from '../components/IELTS/TestCard';
import SectionCard from '../components/IELTS/SectionCard';
import SectionalTestCard from '../components/IELTS/SectionalTestCard';
import {
  CalculatorIcon,
  CalendarIcon,
  ListeningIcon,
  ReadingIcon,
  SpeakingIcon,
  TargetIcon,
  WritingIcon,
} from '../components/IELTS/icons';
import { normalizeIeltsTestKind, sections, testFilters } from '../components/IELTS/mockData';
import { Base_URL } from '../Client/apiURL';
import { themeColors } from '../components/IELTS/styles/theme';
import { formatIELTSBandScore } from '../components/IELTS/utils/scoreHelpers';

const isNotChrome = () => {
  const userAgent = navigator.userAgent;
  return !/Chrome/.test(userAgent) || /OPR|Edge/.test(userAgent);
};

const PageShell = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fbff 0%, #f7f1ff 48%, #fffaf2 100%)',
  paddingTop: '82px',
  [theme.breakpoints.down('md')]: {
    paddingTop: '72px',
  },
}));

const DashboardContainer = styled(Container)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '220px minmax(0, 1fr)',
  gap: '16px',
  paddingBottom: '28px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
}));

const SidebarCard = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: '88px',
  alignSelf: 'start',
  borderRadius: '18px',
  padding: '12px',
  border: '1px solid rgba(153, 108, 254, 0.14)',
  boxShadow: '0 14px 30px rgba(41, 22, 89, 0.07)',
  [theme.breakpoints.down('md')]: {
    position: 'static',
  },
}));

const MainCard = styled(Paper)({
  borderRadius: '20px',
  padding: '18px',
  border: '1px solid rgba(153, 108, 254, 0.12)',
  boxShadow: '0 14px 34px rgba(41, 22, 89, 0.07)',
  background: 'rgba(255, 255, 255, 0.94)',
});

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: '12px',
  padding: '9px 11px',
  marginBottom: '6px',
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 800,
  color: active ? '#fff' : '#3a2f55',
  background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : 'transparent',
  boxShadow: active ? '0 8px 18px rgba(153, 108, 254, 0.24)' : 'none',
  '&:hover': {
    background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : '#f5f0ff',
  },
}));

const GroupCard = styled(Paper)({
  borderRadius: '15px',
  padding: '14px',
  marginBottom: '12px',
  border: '1px solid #edf0f7',
  boxShadow: '0 6px 18px rgba(29, 22, 54, 0.045)',
});

const TestGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  marginTop: '12px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const FullTestItem = styled(Box)({
  minWidth: 0,
});

const kindMap = {
  Cambridge: 'cambridge',
  'Practice Plus': 'practice plus',
  'Recent Actual Test': 'recent actual test',
};

const DASHBOARD_VIEWS = ['dashboard', 'sectional', 'full'];
const DASHBOARD_OFFER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const DASHBOARD_OFFER_URGENCY_DAYS = 3;
const DASHBOARD_OFFER_URGENCY_HOURS = 24;

function getDashboardOfferStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-start-at-${userId}`;
}

function getDashboardOfferBarStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-bar-visible-${userId}`;
}

function getOneOnOneCreditStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-1on1-credit-used-${userId}`;
}

function formatDashboardOfferTime(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function getDashboardViewFromParams(searchParams) {
  const view = searchParams.get('view');
  return DASHBOARD_VIEWS.includes(view) ? view : 'dashboard';
}

function getKindFromParams(searchParams) {
  const kind = searchParams.get('kind');
  return testFilters.find((filter) => filter.value === kind)?.value || testFilters[0].value;
}

function getSectionFromParams(searchParams) {
  const section = searchParams.get('section');
  return sections.find((item) => item.title === section)?.title || sections[0].title;
}

function getGroupId(test) {
  return test?.groupId || test?.group_id || test?.groupID || null;
}

function getStoredGroupName(test) {
  return test?.group_name || test?.groupName || '';
}

function getRawTestKind(test) {
  return test?.test_kind || test?.testKind || test?.test_kind_name || test?.testKindName || '';
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function usesShiftedGroupPrefix(kindLabel) {
  return kindLabel === 'Practice Plus' || kindLabel === 'Recent Actual Test';
}

function getGroupPrefix(groupId, kindLabel = '') {
  if (!groupId) return 'individual';
  const raw = String(groupId).trim();
  const digits = raw.replace(/\D/g, '');
  if (usesShiftedGroupPrefix(kindLabel) && digits.length >= 3) {
    return digits.slice(1, 3);
  }
  return (digits || raw).slice(0, 2);
}

function getKindLabel(test) {
  const explicitKind = getRawTestKind(test);
  if (explicitKind) {
    return normalizeIeltsTestKind(explicitKind);
  }

  const groupName = getStoredGroupName(test);
  const lowerName = groupName.toLowerCase();
  const compactName = lowerName.replace(/[^a-z0-9]/g, '');
  if (lowerName.includes('cambridge')) return 'Cambridge';
  if (lowerName.includes('idp') || compactName.includes('recentactualtest')) return 'Recent Actual Test';
  if (compactName.includes('britishcouncil') || compactName.includes('practiceplus')) return 'Practice Plus';

  const withoutNumbers = groupName.replace(/\d+/g, '').trim();
  return titleCase(withoutNumbers) || 'Group';
}

function isFreeCambridgeSectionalTest(test) {
  const category = String(test?.category_name || '').toLowerCase();
  const isSectionalCategory = ['listening', 'reading', 'writing', 'speaking'].includes(category);
  return getKindLabel(test) === 'Cambridge' && isSectionalCategory;
}

function getGroupKey(test, includeIndividual) {
  const rawGroupId = getGroupId(test);
  if (!rawGroupId && !includeIndividual) return null;
  if (!rawGroupId) return 'individual';
  const kindLabel = getKindLabel(test);
  const prefix = getGroupPrefix(rawGroupId, kindLabel);
  return `${kindLabel.toLowerCase()}-${prefix}`;
}

function getGroupDisplayName(groupKey, groupTests) {
  if (groupKey === 'individual') return 'Individual Tests';
  const first = groupTests[0] || {};
  const kindLabel = getKindLabel(first);
  return `${kindLabel} ${getGroupPrefix(getGroupId(first), kindLabel)}`;
}

function getFullTestName(test, seriesName) {
  return getStoredGroupName(test) || `${seriesName} - Test`;
}

function getFullTestSortNumber(fullTest) {
  const namedMatch = String(fullTest.groupName || '').match(/test\s*(\d+)/i);
  if (namedMatch) return Number(namedMatch[1]);

  const groupIdDigits = String(fullTest.groupId || '').replace(/\D/g, '');
  if (groupIdDigits.length > 2) return Number(groupIdDigits.slice(2)) || 0;
  return Number(groupIdDigits) || 0;
}

function getTestName(test) {
  return test?.test_name || test?.name || test?.title || `Test ${test?.id || ''}`;
}

function getTestTypeLabel(test) {
  if (test?.test_type === 'full' || test?.full_test_attempt_id) return 'Full Test';
  return test?.category_name || 'Sectional';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString();
}

function formatTime(totalTime, fallback = '00:30m') {
  if (!totalTime) return fallback;
  if (totalTime >= 60) return `${Math.floor(totalTime / 60)}h:${String(totalTime % 60).padStart(2, '0')}m`;
  return `00:${String(totalTime).padStart(2, '0')}m`;
}

function formatResultScore(test) {
  const score = test?.band_score ?? test?.score ?? test?.totalMarksObtained ?? test?.total_marks_obtained;
  return formatIELTSBandScore(score);
}

function groupByPrefix(items, includeIndividual = false) {
  const groups = new Map();
  items.forEach((test) => {
    const key = getGroupKey(test, includeIndividual);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(test);
  });

  return Array.from(groups.entries()).map(([groupKey, groupTests]) => {
    const representative = groupTests[0] || {};
    const rawGroupIds = [...new Set(groupTests.map(getGroupId).filter(Boolean))];
    const primaryGroupId = getGroupId(representative) || rawGroupIds[0] || groupKey;
    const kindLabel = getKindLabel(representative);
    return {
      groupKey,
      groupId: primaryGroupId,
      groupPrefix: getGroupPrefix(primaryGroupId, kindLabel),
      rawGroupIds,
      groupName: getGroupDisplayName(groupKey, groupTests),
      tests: groupTests,
    };
  });
}

function groupFullTestsBySeries(items) {
  const seriesMap = new Map();

  items.forEach((test) => {
    const groupId = getGroupId(test);
    const seriesKey = getGroupKey(test, false);
    if (!groupId || !seriesKey) return;

    if (!seriesMap.has(seriesKey)) {
      const kindLabel = getKindLabel(test);
      seriesMap.set(seriesKey, {
        groupKey: seriesKey,
        groupPrefix: getGroupPrefix(groupId, kindLabel),
        groupName: getGroupDisplayName(seriesKey, [test]),
        fullTestsMap: new Map(),
      });
    }

    const series = seriesMap.get(seriesKey);
    const fullTestKey = String(groupId);
    if (!series.fullTestsMap.has(fullTestKey)) {
      series.fullTestsMap.set(fullTestKey, {
        groupKey: `${seriesKey}-${fullTestKey}`,
        groupId,
        groupName: getFullTestName(test, series.groupName),
        tests: [],
      });
    }
    series.fullTestsMap.get(fullTestKey).tests.push(test);
  });

  return Array.from(seriesMap.values()).map((series) => ({
    ...series,
    fullTests: Array.from(series.fullTestsMap.values()).sort(
      (a, b) => getFullTestSortNumber(b) - getFullTestSortNumber(a)
    ),
  }));
}

const IELTSDashboardV2 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsSignature = searchParams.toString();
  const [activeView, setActiveView] = useState(() => getDashboardViewFromParams(searchParams));
  const [open, setOpen] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(() => getKindFromParams(searchParams));
  const [selectedSection, setSelectedSection] = useState(() => getSectionFromParams(searchParams));
  const [loadingTestId, setLoadingTestId] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(() => {
    const stored = localStorage.getItem('ielts-type');
    return stored ? stored.toLowerCase() : 'academic';
  });
  const [profileData, setProfileData] = useState({
    averageScore: '- / 9',
    targetScore: '- / 9',
    examIn: '- Days',
    loading: true,
    error: null,
  });
  const [showTargetScoreModal, setShowTargetScoreModal] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);
  const [newTargetScore, setNewTargetScore] = useState('');
  const [newExamDate, setNewExamDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOfferBar, setShowOfferBar] = useState(false);
  const [offerStartTs, setOfferStartTs] = useState(null);
  const [offerNowTs, setOfferNowTs] = useState(() => Date.now());
  const [offerCodeCopied, setOfferCodeCopied] = useState(false);
  const [showBookClassModal, setShowBookClassModal] = useState(false);
  const [isOneOnOneCreditUsed, setIsOneOnOneCreditUsed] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isSubscribed = userData?.IsSubscribed || false;

  useEffect(() => {
    const isUsed = localStorage.getItem(getOneOnOneCreditStorageKey()) === '1';
    setIsOneOnOneCreditUsed(isUsed);
  }, []);

  useEffect(() => {
    if (isSubscribed) return;
    const storageKey = getDashboardOfferStorageKey();
    const storedStart = Number(localStorage.getItem(storageKey));
    const hasValidStoredStart = Number.isFinite(storedStart) && storedStart > 0;
    const startTs = hasValidStoredStart ? storedStart : Date.now();

    if (!hasValidStoredStart) {
      localStorage.setItem(storageKey, String(startTs));
    }

    setOfferStartTs(startTs);
    setOfferNowTs(Date.now());
    setShowOfferModal(true);
    setShowOfferBar(localStorage.getItem(getDashboardOfferBarStorageKey()) === '1');

    const timerId = setInterval(() => {
      setOfferNowTs(Date.now());
    }, 1000);

    return () => clearInterval(timerId);
  }, [isSubscribed]);

  const offerRemainingMs = useMemo(() => {
    if (!offerStartTs) return DASHBOARD_OFFER_DURATION_MS;
    const expiryTime = offerStartTs + DASHBOARD_OFFER_DURATION_MS;
    return expiryTime - offerNowTs;
  }, [offerStartTs, offerNowTs]);

  const isOfferExpired = offerRemainingMs <= 0;
  const isOfferFinalHours = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_HOURS * 60 * 60 * 1000;
  const isOfferFinalDays = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_DAYS * 24 * 60 * 60 * 1000;
  const offerUrgencyLabel = isOfferFinalHours ? 'Final hours' : isOfferFinalDays ? 'Ending soon' : 'Limited offer';

  const handleDismissOfferModal = () => {
    setShowOfferModal(false);
    if (!isSubscribed && !isOfferExpired) {
      setShowOfferBar(true);
      localStorage.setItem(getDashboardOfferBarStorageKey(), '1');
    }
  };

  const handleCopyOfferCode = async () => {
    if (isOfferExpired) return;
    try {
      await navigator.clipboard.writeText('learnswift20');
      setOfferCodeCopied(true);
      setTimeout(() => setOfferCodeCopied(false), 1800);
    } catch (error) {
      setOfferCodeCopied(false);
    }
  };

  const handleOpenPlanInfo = () => {
    setShowOfferModal(false);
    setShowOfferBar(false);
    localStorage.removeItem(getDashboardOfferBarStorageKey());
    navigate('/sidebar', { state: { section: 'Plan Info' } });
  };

  const handleConfirmBookOneOnOne = () => {
    localStorage.setItem(getOneOnOneCreditStorageKey(), '1');
    setIsOneOnOneCreditUsed(true);
    setShowBookClassModal(false);
    window.open('https://calendly.com/usmanmeher007/swiftuni-1-on-1', '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsSignature);
    const nextView = getDashboardViewFromParams(currentParams);
    const nextKind = getKindFromParams(currentParams);
    const nextSection = getSectionFromParams(currentParams);

    setActiveView((current) => (current === nextView ? current : nextView));
    setSelectedKind((current) => (current === nextKind ? current : nextKind));
    setSelectedSection((current) => (current === nextSection ? current : nextSection));
  }, [searchParamsSignature]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParamsSignature);

    if (activeView === 'dashboard') {
      nextParams.delete('view');
    } else {
      nextParams.set('view', activeView);
    }

    if (selectedKind === testFilters[0].value) {
      nextParams.delete('kind');
    } else {
      nextParams.set('kind', selectedKind);
    }

    if (activeView === 'sectional' && selectedSection !== sections[0].title) {
      nextParams.set('section', selectedSection);
    } else {
      nextParams.delete('section');
    }

    const nextSignature = nextParams.toString();
    if (nextSignature !== searchParamsSignature) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeView, selectedKind, selectedSection, searchParamsSignature, setSearchParams]);

  const fetchProfileData = useCallback(async () => {
    try {
      setProfileData((prev) => ({ ...prev, loading: true, error: null }));
      const response = await fetch(`${Base_URL}/app/users/get-ielts-profile`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to fetch profile data');
      const profile = data.response;
      const averageScore = formatIELTSBandScore(profile.average_score);
      setProfileData({
        averageScore: averageScore !== '-' ? `${averageScore} / 9` : '- / 9',
        targetScore: profile.ielts_target_score ? `${profile.ielts_target_score} / 9` : '- / 9',
        examIn: profile.days_until_exam ? `${String(profile.days_until_exam).padStart(2, '0')} Days` : '- Days',
        loading: false,
        error: null,
      });
    } catch (err) {
      setProfileData((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  }, []);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const testsArr = Array.isArray(data) ? data : Array.isArray(data.response) ? data.response : [];
      setTests(testsArr);
    } catch (err) {
      setError('Failed to fetch IELTS tests');
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getUserTestsInfo`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setResults(data.response?.tests || []);
    } catch (err) {
      setResults([]);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    setOpen(isNotChrome());
    const localUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    setShowFooter((localUserData?.PortalId || 0) === 1);
    localStorage.setItem('exam-type', 'ielts');
    if (!localStorage.getItem('ielts-type')) localStorage.setItem('ielts-type', 'academic');
    window.dispatchEvent(new CustomEvent('examTypeChanged', { detail: { examType: 'ielts' } }));

    fetchTests();
    fetchProfileData();
    fetchResults();

    const handleIeltsTypeChange = (event) => {
      const { ieltsType } = event.detail || {};
      if (ieltsType) setSelectedTestType(ieltsType.toLowerCase());
    };
    window.addEventListener('ieltsTypeChanged', handleIeltsTypeChange);
    return () => window.removeEventListener('ieltsTypeChanged', handleIeltsTypeChange);
  }, [fetchTests, fetchProfileData, fetchResults]);

  const sectionCounts = useMemo(() => {
    const counts = { Listening: 0, Reading: 0, Writing: 0, Speaking: 0 };
    tests.forEach((test) => {
      const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
      const matchesSelectedKind = getKindLabel(test).toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase());
      if (!matchesType || !matchesSelectedKind || !test.category_name) return;
      const category = test.category_name.toLowerCase();
      if (category.includes('listening')) counts.Listening += 1;
      else if (category.includes('reading')) counts.Reading += 1;
      else if (category.includes('writing')) counts.Writing += 1;
      else if (category.includes('speaking')) counts.Speaking += 1;
    });
    return counts;
  }, [tests, selectedKind, selectedTestType]);

  const matchesKind = useCallback((test) => {
    return getKindLabel(test).toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase());
  }, [selectedKind]);

  const matchesType = useCallback(
    (test) => (test.test_type ? test.test_type.toLowerCase() === selectedTestType : true),
    [selectedTestType]
  );

  const fullMockSeries = useMemo(() => {
    const groupedSeries = groupFullTestsBySeries(
      tests.filter((test) => getGroupId(test) && matchesKind(test) && matchesType(test))
    );

    return groupedSeries.map((series) => ({
      ...series,
      fullTests: series.fullTests.map((fullTest) => {
        const categories = [...new Set(fullTest.tests.map((test) => test.category_name))].filter(Boolean);
        const representative = fullTest.tests[0];
        const totalDuration = fullTest.tests.reduce((sum, test) => sum + (test.total_time || 0), 0);
        const requiredCategories = ['Listening', 'Reading', 'Writing'];
        const lowerCategories = categories.map((category) => category.toLowerCase());
        const missingCategories = requiredCategories.filter((category) => !lowerCategories.includes(category.toLowerCase()));

        return {
          ...fullTest,
          representative,
          categories,
          totalDuration,
          missingCategories,
          hasAllRequiredCategories: missingCategories.length === 0,
          isUnlocked: isSubscribed,
          hasInProgress: fullTest.tests.some((test) => test.has_in_progress_full_attempt || test.resume_full_test_attempt_id),
          latestSummary: fullTest.tests.find((test) => test.latest_full_attempt_summary)?.latest_full_attempt_summary || null,
          resumeAttemptId: fullTest.tests.find((test) => test.resume_full_test_attempt_id)?.resume_full_test_attempt_id || null,
        };
      }),
    }));
  }, [tests, matchesKind, matchesType, isSubscribed]);

  const sectionalGroups = useMemo(() => {
    const filtered = tests.filter((test) => {
      const sectionMatch = test.category_name?.toLowerCase() === selectedSection.toLowerCase();
      return sectionMatch && matchesKind(test) && matchesType(test);
    });
    return groupByPrefix(filtered, true).map((group) => ({
      ...group,
      tests: group.tests.map((test) => ({
        ...test,
        isUnlocked: isSubscribed || isFreeCambridgeSectionalTest(test),
      })),
    }));
  }, [tests, matchesKind, matchesType, selectedSection, isSubscribed]);

  const lastFourResults = useMemo(() => (results || []).slice(0, 4), [results]);

  const navigateToResultDetails = (result) => {
    if (result?.test_type === 'full' || result?.full_test_attempt_id || result?.fullTestAttemptId) {
      const fullTestAttemptId = result.full_test_attempt_id || result.fullTestAttemptId;
      if (fullTestAttemptId) {
        navigate(`/ielts-test-details?type=full&fullTestAttemptId=${fullTestAttemptId}`);
        return;
      }
    }

    const attemptId = result?.attempt_id || result?.attemptId;
    if (attemptId) {
      navigate(`/ielts-test-details?type=individual&attemptId=${attemptId}`);
      return;
    }

    navigate('/ielts-results');
  };

  const getSectionIcon = (iconName) => {
    switch (iconName) {
      case 'listening': return <ListeningIcon sx={{ fontSize: 22 }} />;
      case 'reading': return <ReadingIcon sx={{ fontSize: 22 }} />;
      case 'writing': return <WritingIcon sx={{ fontSize: 22 }} />;
      case 'speaking': return <SpeakingIcon sx={{ fontSize: 22 }} />;
      default: return null;
    }
  };

  const resolveFullTestResumeAttemptId = (group) => Number(
    group.resumeAttemptId || group.latestSummary?.full_test_attempt_id || 0
  );

  const handleFullTestResume = async (group) => {
    const attemptId = resolveFullTestResumeAttemptId(group);
    if (!attemptId) {
      alert('No in-progress attempt to resume.');
      return;
    }
    const loadingKey = `full-${group.groupKey}-resume`;
    setLoadingTestId(loadingKey);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/full-attempts/${attemptId}/resume-context`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const payload = data?.response;
      if (!response.ok || data?.responseCode !== 200 || !payload?.tests?.length) {
        throw new Error(data?.message || 'Unable to load full test for resume');
      }
      navigate('/ielts-full-test', {
        state: {
          mode: 'practice',
          groupedTests: group.tests,
          groupId: Number(group.groupId),
          continuePrevious: true,
          resumeFullTestAttemptId: Number(attemptId),
          prefetchedResumeContext: { ...payload, fullTestAttemptId: Number(attemptId) },
        },
      });
    } catch (err) {
      alert(err.message || 'Unable to resume full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleFullTestStart = async (group) => {
    const shouldResume = Boolean(group.hasInProgress || String(group.latestSummary?.status || '').toLowerCase() === 'in_progress');
    const loadingKey = `full-${group.groupKey}-start`;
    setLoadingTestId(loadingKey);
    try {
      if (shouldResume) {
        const response = await fetch(`${Base_URL}/app/users/ielts-tests/full-attempts/start`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId: group.groupId }),
        });
        const data = await response.json();
        if (data.responseCode !== 200 || !data.response?.fullTestAttemptId) {
          throw new Error(data.message || 'Could not start a new attempt');
        }
        navigate('/ielts-full-test', {
          state: {
            mode: 'practice',
            groupedTests: group.tests,
            groupId: group.groupId,
            continuePrevious: false,
            fullTestAttemptId: data.response.fullTestAttemptId,
          },
        });
        return;
      }
      navigate('/ielts-full-test', {
        state: { mode: 'practice', groupedTests: group.tests, groupId: group.groupId },
      });
    } catch (err) {
      alert(err.message || 'Failed to start full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleStartTest = async (testId, mode = 'practice') => {
    const loadingId = `sectional-${testId}`;
    setLoadingTestId(loadingId);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getTestById/${testId}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const test = data.response;
      if (!response.ok || !test) throw new Error('Test not found');

      const state = { duration: test.total_time || 60, mode, testData: test };
      if (test.category_name === 'Listening') navigate('/ielts-listening-test', { state: { ...state, duration: 30 } });
      else if (test.category_name === 'Writing') navigate('/ielts-writing-test', { state: { ...state, duration: 60 } });
      else if (test.category_name === 'Reading') navigate('/ielts-reading-test', { state: { ...state, duration: 60 } });
      else if (test.category_name === 'Speaking') navigate('/ielts-speaking-test', { state: { ...state, duration: 15, currentUserId: userData?.UserId || 1 } });
    } catch (err) {
      alert('Failed to load test details. Please try again.');
    } finally {
      setLoadingTestId(null);
    }
  };

  const updateTargetScore = async () => {
    if (!newTargetScore) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_target_score: parseFloat(newTargetScore) }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update target score');
      await fetchProfileData();
      setShowTargetScoreModal(false);
      setNewTargetScore('');
    } catch (err) {
      alert('Failed to update target score. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const updateExamDate = async () => {
    if (!newExamDate) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_exam_date: newExamDate }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update exam date');
      await fetchProfileData();
      setShowExamDateModal(false);
      setNewExamDate('');
    } catch (err) {
      alert('Failed to update exam date. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const renderSidebar = () => (
    <SidebarCard>
      <NavButton active={activeView === 'dashboard'} onClick={() => setActiveView('dashboard')}>
        Dashboard
      </NavButton>
      <NavButton active={false} onClick={() => navigate('/ielts-results')}>
        Results
      </NavButton>
      <Divider sx={{ my: 1 }} />
      <NavButton active={activeView === 'sectional'} onClick={() => setActiveView('sectional')}>
        Sectional Mock Test
      </NavButton>
      <NavButton active={activeView === 'full'} onClick={() => setActiveView('full')}>
        Full Mock Test
      </NavButton>
    </SidebarCard>
  );

  const renderFilterRow = () => (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {testFilters.map((filter) => (
        <Button
          key={filter.value}
          variant={selectedKind === filter.value ? 'contained' : 'outlined'}
          onClick={() => setSelectedKind(filter.value)}
          size="small"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: selectedKind === filter.value ? themeColors.primary : '#fff',
            borderColor: 'rgba(153,108,254,0.35)',
            color: selectedKind === filter.value ? '#fff' : '#4b3d72',
            '&:hover': {
              backgroundColor: selectedKind === filter.value ? themeColors.primaryHover : '#f7f1ff',
              borderColor: themeColors.primary,
            },
          }}
        >
          {filter.label}
        </Button>
      ))}
    </Stack>
  );

  const renderScoreCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Average Score" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.averageScore} icon={<CalculatorIcon sx={{ fontSize: 24, color: '#FF6B6B' }} />} bgcolor="#FFEAEA" />
      </Grid>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Target Score" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.targetScore} icon={<TargetIcon sx={{ fontSize: 24, color: themeColors.primary }} />} bgcolor={themeColors.primaryLighter} showChangeButton onChangeClick={() => setShowTargetScoreModal(true)} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ScoreCard title="Exam In" score={profileData.loading ? 'Loading...' : profileData.error ? 'Error' : profileData.examIn} icon={<CalendarIcon sx={{ fontSize: 24, color: '#00B4D8' }} />} bgcolor="#E1F9FF" showChangeButton onChangeClick={() => setShowExamDateModal(true)} />
      </Grid>
    </Grid>
  );

  const renderRecentResults = () => (
    <GroupCard>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#221a3a' }}>
          Last 4 Test Results
        </Typography>
        <Button onClick={() => navigate('/ielts-results')} size="small" sx={{ textTransform: 'none', fontWeight: 800 }}>
          View all
        </Button>
      </Box>
      {resultsLoading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
      ) : lastFourResults.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 2, sm: 2.4 },
            borderRadius: '14px',
            border: '1px dashed rgba(153, 108, 254, 0.38)',
            background: 'linear-gradient(135deg, #fbf8ff 0%, #f6fbff 100%)',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 900, color: '#2d2547', mb: 0.5 }}>
            No IELTS results yet
          </Typography>
          <Typography variant="body2" sx={{ color: '#6d647d', maxWidth: 460, mx: 'auto' }}>
            Start a sectional or full mock test, and your latest scores will appear here.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.4}>
          {lastFourResults.map((result, index) => {
            const score = formatResultScore(result);
            const status = result.status || 'completed';
            return (
              <Grid item xs={12} sm={6} key={result.testId || result.testid || result.attempt_id || result.full_test_attempt_id || index}>
                <Paper
                  onClick={() => navigateToResultDetails(result)}
                  sx={{
                    height: '100%',
                    minHeight: 132,
                    p: 1.7,
                    borderRadius: '14px',
                    border: '1px solid rgba(153, 108, 254, 0.16)',
                    cursor: 'pointer',
                    background: 'linear-gradient(145deg, #ffffff 0%, #fbf8ff 100%)',
                    boxShadow: '0 10px 22px rgba(41, 22, 89, 0.06)',
                    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'rgba(153, 108, 254, 0.46)',
                      boxShadow: '0 14px 28px rgba(41, 22, 89, 0.11)',
                    },
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.2} mb={1.2}>
                    <Chip
                      label={getTestTypeLabel(result)}
                      size="small"
                      sx={{
                        maxWidth: '70%',
                        fontWeight: 800,
                        color: '#5d42a5',
                        backgroundColor: '#f0eaff',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        color: '#1f7a4d',
                        backgroundColor: '#e8f8ef',
                        textTransform: 'capitalize',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 900,
                      color: '#221a3a',
                      mb: 0.7,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 40,
                    }}
                  >
                    {getTestName(result)}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={1}>
                    <Typography variant="caption" sx={{ color: '#746b86', fontWeight: 700 }}>
                      {formatDate(result.date_time)}
                    </Typography>
                    <Box textAlign="right">
                      <Typography variant="caption" sx={{ color: '#746b86', fontWeight: 700, display: 'block' }}>
                        Score
                      </Typography>
                      <Typography sx={{ fontWeight: 950, color: '#996CFE', fontSize: 20, lineHeight: 1 }}>
                        {score === '-' ? 'N/A' : score}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </GroupCard>
  );

  const renderDashboard = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936' }}>
          IELTS Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
          Track your target, exam timeline, and latest IELTS test activity.
        </Typography>
      </Box>
      {renderScoreCards()}
      {renderRecentResults()}
    </>
  );

  const renderFullTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936', mb: 0.4 }}>
        Full Mock Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Full mocks are grouped by test type and the first two digits of group ID.
      </Typography>
      {renderFilterRow()}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : fullMockSeries.length === 0 ? (
        <Typography color="text.secondary">No full mock tests available.</Typography>
      ) : (
        fullMockSeries.map((series) => (
          <GroupCard key={`full-series-${series.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={0.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#2d2547' }}>{series.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {series.fullTests.length} full mock tests
                </Typography>
              </Box>
              <Chip label={`Group ${series.groupPrefix}`} size="small" sx={{ fontWeight: 800 }} />
            </Box>
            <TestGrid>
              {series.fullTests.map((group) => {
                const disabledMessage = group.hasAllRequiredCategories ? null : `Under development. ${group.missingCategories.join(', ')} sections are being added.`;
                const shouldResume = Boolean(group.hasInProgress || String(group.latestSummary?.status || '').toLowerCase() === 'in_progress');
                return (
                  <FullTestItem key={`full-${group.groupKey}`}>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {group.categories.map((category) => <Chip key={category} label={category} size="small" />)}
                    </Stack>
                    <TestCard
                      title={group.groupName}
                      time={formatTime(group.totalDuration, '2h:30m')}
                      difficulty={group.representative?.test_type || 'N/A'}
                      onStart={() => handleFullTestStart(group)}
                      onResume={() => handleFullTestResume(group)}
                      showResumeButton={shouldResume}
                      loadingStart={loadingTestId === `full-${group.groupKey}-start`}
                      loadingResume={loadingTestId === `full-${group.groupKey}-resume`}
                      isLocked={!group.isUnlocked}
                      disabledMessage={disabledMessage}
                    />
                  </FullTestItem>
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderSectionalTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#211936', mb: 0.4 }}>
        Sectional Mock Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sectional tests are grouped by test type and the first two digits of group ID.
      </Typography>
      {renderFilterRow()}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {sections.map((section) => (
          <Grid item xs={6} md={3} key={section.id}>
            <SectionCard
              title={section.title}
              count={sectionCounts[section.title] || 0}
              icon={getSectionIcon(section.icon)}
              onClick={() => setSelectedSection(section.title)}
              isSelected={selectedSection === section.title}
            />
          </Grid>
        ))}
      </Grid>
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : sectionalGroups.length === 0 ? (
        <Typography color="text.secondary">No sectional tests available.</Typography>
      ) : (
        sectionalGroups.map((group) => (
          <GroupCard key={`sectional-${group.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={1}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#2d2547' }}>{group.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.groupKey === 'individual' ? 'Standalone tests' : `Group prefix: ${group.groupPrefix}`}
                </Typography>
              </Box>
              <Chip label={selectedSection} color="primary" size="small" />
            </Box>
            <TestGrid>
              {group.tests.map((test) => {
                const testName = getTestName(test);
                return (
                  <SectionalTestCard
                    key={test.id}
                    title={testName}
                    time={formatTime(test.total_time, selectedSection === 'Reading' || selectedSection === 'Writing' ? '01:00h' : selectedSection === 'Speaking' ? '00:15m' : '00:30m')}
                    difficulty={test.test_type || 'N/A'}
                    onStart={() => handleStartTest(test.id)}
                    selectedSection={selectedSection}
                    isLocked={!test.isUnlocked}
                    loading={loadingTestId === `sectional-${test.id}`}
                  />
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderTargetScoreModal = () => (
    <Dialog open={showTargetScoreModal} onClose={() => setShowTargetScoreModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Target Score</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Target Score</InputLabel>
          <Select value={newTargetScore} label="Target Score" onChange={(event) => setNewTargetScore(event.target.value)}>
            {[4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0].map((score) => (
              <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateTargetScore} variant="contained" disabled={!newTargetScore || isUpdating}>Update</Button>
        <Button onClick={() => setShowTargetScoreModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const renderExamDateModal = () => (
    <Dialog open={showExamDateModal} onClose={() => setShowExamDateModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Exam Date</DialogTitle>
      <DialogContent>
        <TextField
          label="Exam Date"
          type="date"
          value={newExamDate}
          onChange={(event) => setNewExamDate(event.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateExamDate} variant="contained" disabled={!newExamDate || isUpdating}>Update</Button>
        <Button onClick={() => setShowExamDateModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const renderDashboardOfferModal = () => (
    <Dialog open={showOfferModal && !isSubscribed} onClose={handleDismissOfferModal} maxWidth="sm" fullWidth>
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 4 },
          background: 'linear-gradient(145deg, #fffaf3 0%, #f6f0ff 52%, #eef9ff 100%)',
        }}
      >
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: '20px',
            display: 'grid',
            placeItems: 'center',
            mb: 2,
            color: '#fff',
            background: 'linear-gradient(135deg, #996CFE 0%, #FF9F43 100%)',
            boxShadow: '0 14px 34px rgba(153, 108, 254, 0.28)',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 34 }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 900, color: '#20153d', mb: 0.8, fontSize: { xs: 26, sm: 32 } }}>
          IELTS Success Offer - 20% Off
        </Typography>

        <Typography sx={{ color: '#51476b', fontSize: 15.5, lineHeight: 1.65, mb: 2 }}>
          Upgrade today to accelerate your prep with premium IELTS tools and expert support.
        </Typography>

        {!isOfferExpired && (
          <Box
            sx={{
              mb: 2,
              p: 1.4,
              borderRadius: '12px',
              border: `1px solid ${isOfferFinalHours ? '#FECDD3' : isOfferFinalDays ? '#FDBA74' : '#D6BCFA'}`,
              backgroundColor: isOfferFinalHours ? '#FFF1F3' : isOfferFinalDays ? '#FFF7ED' : '#F4F0FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ color: isOfferFinalHours ? '#B42318' : isOfferFinalDays ? '#C2410C' : '#5B4A7A', fontWeight: 900, fontSize: 13.5 }}>
              {offerUrgencyLabel}: {formatDashboardOfferTime(offerRemainingMs)} left
            </Typography>
            <Typography sx={{ color: isOfferFinalHours ? '#B42318' : isOfferFinalDays ? '#C2410C' : '#5B4A7A', fontWeight: 700, fontSize: 12.5 }}>
              Offer auto-expires in your account
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            p: 1.8,
            mb: 2.2,
            borderRadius: '14px',
            border: '1px solid rgba(153,108,254,0.26)',
            background: 'rgba(255,255,255,0.84)',
            boxShadow: '0 10px 22px rgba(95, 62, 170, 0.12)',
          }}
        >
          <Chip
            label="Most Valuable Benefit"
            size="small"
            sx={{
              mb: 1,
              fontWeight: 800,
              color: '#fff',
              background: 'linear-gradient(135deg, #996CFE 0%, #7351E9 100%)',
            }}
          />
          <Typography sx={{ fontSize: { xs: 22, sm: 28 }, lineHeight: 1.2, color: '#23143f', fontWeight: 900 }}>
            1 Free 1-on-1 IELTS Class
          </Typography>
        </Box>

        <Stack spacing={1.1} sx={{ mb: 2.4 }}>
          {[
            'Unlimited AI checking',
            'Recent Actual Test + Practice Plus access',
            'Goal-focused preparation to help you achieve your target band',
          ].map((text) => (
            <Box key={text} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CheckCircleOutlineIcon sx={{ color: '#37965E', fontSize: 20 }} />
              <Typography sx={{ color: '#33294f', fontWeight: 700, fontSize: 14.5 }}>{text}</Typography>
            </Box>
          ))}
        </Stack>

        {!isOfferExpired && (
          <Box
            sx={{
              p: 2,
              borderRadius: '18px',
              border: `1px dashed ${isOfferFinalHours ? '#FDA4AF' : 'rgba(255, 159, 67, 0.8)'}`,
              background: isOfferFinalHours ? 'rgba(255, 241, 242, 0.95)' : 'rgba(255, 255, 255, 0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferOutlinedIcon sx={{ color: isOfferFinalHours ? '#B42318' : '#FF8A00' }} />
              <Typography sx={{ color: '#4b3d72', fontWeight: 800 }}>
                Use code for 20% off
              </Typography>
            </Box>
            <Chip
              label={offerCodeCopied ? 'Code copied' : 'learnswift20'}
              onClick={handleCopyOfferCode}
              role="button"
              title="Click to copy discount code"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.7,
                color: '#7a3d00',
                background: '#FFE0B2',
                border: '1px solid #FFB74D',
                cursor: 'pointer',
                '&:hover': {
                  background: '#FFD28A',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, pb: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleDismissOfferModal} sx={{ color: '#655b78', fontWeight: 800, textTransform: 'none' }}>
          Maybe later
        </Button>
        <Button
          onClick={handleOpenPlanInfo}
          variant="contained"
          sx={{
            borderRadius: '999px',
            px: 3,
            py: 1,
            fontWeight: 900,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
            boxShadow: '0 10px 22px rgba(153, 108, 254, 0.28)',
          }}
        >
          View subscription plans
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDashboardOfferBar = () => {
    if (isSubscribed || isOfferExpired || !showOfferBar) return null;
    return (
      <Box
        sx={{
          mb: 1.5,
          p: { xs: 1.3, sm: 1.6 },
          borderRadius: '16px',
          border: `1px solid ${isOfferFinalHours ? '#fb7185' : isOfferFinalDays ? '#f59e0b' : '#a78bfa'}`,
          background: isOfferFinalHours
            ? 'linear-gradient(135deg, #fff1f3 0%, #ffe4e8 42%, #ffe2e6 100%)'
            : isOfferFinalDays
              ? 'linear-gradient(135deg, #fff7ed 0%, #ffefdc 48%, #ffe8ce 100%)'
              : 'linear-gradient(135deg, #f6f1ff 0%, #eef5ff 52%, #e8fbff 100%)',
          boxShadow: isOfferFinalHours
            ? '0 14px 30px rgba(244, 63, 94, 0.18)'
            : '0 14px 30px rgba(124, 58, 237, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1.2,
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
          <AutoAwesomeIcon sx={{ color: isOfferFinalHours ? '#e11d48' : '#7c3aed', fontSize: 22 }} />
          <Box>
            <Typography sx={{ color: '#201238', fontWeight: 950, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.1 }}>
              1 Free 1-on-1 Class
            </Typography>
            <Typography sx={{ color: '#5b3d9a', fontWeight: 900, fontSize: 12.8 }}>
              Plus 20% off with code learnswift20
            </Typography>
            <Typography sx={{ color: '#564b70', fontWeight: 700, fontSize: 12.2 }}>
              {offerUrgencyLabel}: {formatDashboardOfferTime(offerRemainingMs)} left
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ ml: 'auto' }}>
          <Chip
            label={offerCodeCopied ? 'Code copied' : 'learnswift20'}
            onClick={handleCopyOfferCode}
            sx={{
              fontWeight: 900,
              cursor: 'pointer',
              color: '#7a3d00',
              background: '#FFE0B2',
              border: '1px solid #FFB74D',
              '&:hover': { background: '#FFD28A' },
            }}
          />
          <Button
            onClick={() => setShowOfferModal(true)}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 800, color: '#5e3ec8' }}
          >
            View offer
          </Button>
          <Button
            onClick={handleOpenPlanInfo}
            size="small"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: '999px',
              background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
            }}
          >
            View plans
          </Button>
          <IconButton
            size="small"
            onClick={() => {
              setShowOfferBar(false);
              localStorage.removeItem(getDashboardOfferBarStorageKey());
            }}
            sx={{ color: '#6b5a8f', border: '1px solid rgba(91, 74, 122, 0.2)' }}
            title="Hide offer bar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const renderPaidHeaderAction = () => {
    if (!isSubscribed) return null;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.2 }}>
        <Button
          variant="contained"
          disabled={isOneOnOneCreditUsed}
          onClick={() => setShowBookClassModal(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: '999px',
            px: 2.1,
            py: 0.85,
            background: isOneOnOneCreditUsed
              ? '#b8b8c6'
              : 'linear-gradient(135deg, #00a86b 0%, #0d8f76 100%)',
            boxShadow: isOneOnOneCreditUsed
              ? 'none'
              : '0 10px 20px rgba(13, 143, 118, 0.24)',
          }}
        >
          {isOneOnOneCreditUsed ? '1-on-1 Class Used' : 'Book 1-on-1'}
        </Button>
      </Box>
    );
  };

  const renderBookClassModal = () => (
    <Dialog open={showBookClassModal} onClose={() => setShowBookClassModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, color: '#1f1540' }}>Book Your 1-on-1 Class</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#473c64', mb: 1.2 }}>
          You are about to use your included 1-on-1 class credit.
        </Typography>
        <Typography sx={{ color: '#473c64', mb: 1.2 }}>
          Please use the same email address that you use for your SwiftUni account while booking.
        </Typography>
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          Once you continue to Calendly, this class credit will be marked as used and cannot be booked again.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.4 }}>
        <Button onClick={() => setShowBookClassModal(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmBookOneOnOne}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #00a86b 0%, #0d8f76 100%)',
          }}
        >
          Continue to Booking
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      <BrowserNotSupported open={open} close={setOpen} />
      <Navbar />
      <PageShell>
        <DashboardContainer maxWidth="xl">
          {renderSidebar()}
          <MainCard>
            {renderPaidHeaderAction()}
            {renderDashboardOfferBar()}
            {activeView === 'dashboard' && renderDashboard()}
            {activeView === 'sectional' && renderSectionalTests()}
            {activeView === 'full' && renderFullTests()}
          </MainCard>
        </DashboardContainer>
      </PageShell>
      {showFooter && (
        <Box sx={{ padding: '0rem 3% 0rem', background: 'white' }}>
          <Footer />
        </Box>
      )}
      {renderTargetScoreModal()}
      {renderExamDateModal()}
      {renderDashboardOfferModal()}
      {renderBookClassModal()}
    </Box>
  );
};

export default IELTSDashboardV2;
